import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PageContainer, PageCard, PageHeader, PageHeaderContent, PageHeaderTitle, PageHeaderHeading, PageHeaderDescription } from '@/components/ui/page'
import { Phone, PhoneOutgoing, CheckCircle2, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useAgents } from '@/hooks/useAgents'
import { useInitiateOutboundCall } from '@/hooks/useOutboundCalls'
import { AgentType } from '@/api/generated/models'

export const Route = createFileRoute('/kampangen')({
  component: Kampangen,
  errorComponent: RouteErrorFallback,
})

function Kampangen() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedAgentId, setSelectedAgentId] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const { data: agents, isLoading: isLoadingAgents } = useAgents()
  const initiateCall = useInitiateOutboundCall()

  // Filter agents to only show outbound or bidirectional
  const outboundAgents = agents?.filter(
    (agent) => agent.agent_type === AgentType.Outbound || agent.agent_type === AgentType.Bidirectional
  ) || []

  const handleInitiateCall = async () => {
    setError(null)
    setSuccess(null)

    if (!selectedAgentId) {
      setError('Bitte wählen Sie einen Agenten aus.')
      return
    }

    if (!phoneNumber || phoneNumber.trim().length === 0) {
      setError('Bitte geben Sie eine Telefonnummer ein.')
      return
    }

    // Combine German prefix with user-entered number
    const fullPhoneNumber = `+49${phoneNumber.trim()}`

    try {
      await initiateCall.mutateAsync({
        agentId: selectedAgentId,
        request: {
          phone_number: fullPhoneNumber,
        },
      })

      setSuccess(`Anruf an ${fullPhoneNumber} wird initiiert.`)

      // Reset form and close dialog after a short delay
      setTimeout(() => {
        setPhoneNumber('')
        setSelectedAgentId('')
        setSuccess(null)
        setIsDialogOpen(false)
      }, 2000)
    } catch (error) {
      setError('Anruf konnte nicht gestartet werden.')
    }
  }

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <div className="flex items-center gap-2">
                <PhoneOutgoing className="h-5 w-5" />
                <PageHeaderHeading>Kampangen</PageHeaderHeading>
              </div>
              <PageHeaderDescription>Verwalten und starten Sie ausgehende Anrufkampagnen</PageHeaderDescription>
            </PageHeaderTitle>
          </PageHeaderContent>
        </PageHeader>
        <div className="flex-1 flex items-center justify-center p-6">
          <Button
            size="lg"
            onClick={() => setIsDialogOpen(true)}
            className="w-full max-w-xs"
          >
            <Phone className="mr-2 h-5 w-5" />
            Anruf starten
          </Button>
        </div>
      </PageCard>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ausgehenden Anruf starten</DialogTitle>
            <DialogDescription>
              Wählen Sie einen Agenten und geben Sie eine Telefonnummer ein.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="agent">Agent</Label>
              <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                <SelectTrigger id="agent">
                  <SelectValue placeholder="Agent auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingAgents ? (
                    <SelectItem value="loading" disabled>
                      Laden...
                    </SelectItem>
                  ) : outboundAgents.length === 0 ? (
                    <SelectItem value="empty" disabled>
                      Keine Agenten verfügbar
                    </SelectItem>
                  ) : (
                    outboundAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefonnummer</Label>
              <div className="flex items-center gap-2">
                <div className="flex h-9 items-center gap-1.5 rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                  <span>🇩🇪</span>
                  <span className="font-medium">+49</span>
                </div>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="1234567890"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleInitiateCall}
              disabled={initiateCall.isPending || !selectedAgentId || !phoneNumber}
            >
              {initiateCall.isPending ? 'Wird gestartet...' : 'Anruf starten'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
